/* Photo Motif — photo upload on the product page.

   THIS FILE IS NOW A REQUIREMENT, WHICH IT DID NOT USE TO BE. It is worth
   knowing why, because the old arrangement was the better one and it was given
   up for a reason.

   The photo used to travel with the cart form as properties[Photo], and this
   script was a courtesy on top: a thumbnail, and three checks the customer
   would otherwise discover after paying. Blocked, the order still worked.

   But a file Shopify takes at /cart/add does not land in Files. It has no file
   id, it cannot be looked up, and fileDelete has nothing to be given — the
   photograph is permanent. Measured, not assumed: see
   backend/tools/probe-file-delete.mjs. That makes the privacy policy's promise
   to delete it after a year impossible to keep.

   So the bytes now go to the studio backend, which puts them in Files through
   the API and answers with an id. Only that id travels, in
   properties[_photo_url]. Without this script there is no id and therefore no
   order — the section renders the buy button disabled and says so in a
   noscript block.

   WHAT THIS FILE IS ACTUALLY FOR, NOW THAT IT IS LOAD-BEARING.

   It is the only thing standing between a stranger and the most irreversible
   act on the site: handing over a photograph that exists nowhere else. So it
   answers. Every refusal says what to try next, nothing is ever taken away
   without a way back, the slowest part of the wait has words on it, and the
   buy button is never a dead tap.

   And it stopped trying to be the authority on what a photograph is. The
   backend reads the first bytes of every file (backend/src/image.mjs) and
   accepts ten HEIF brands, including the generic mif1 and msf1 an iPhone burst
   or Live Photo carries. This file used to compare file.type for exact
   equality against a list of four media types, which refused image/heif and
   image/heic-sequence outright — files the server would have taken, at the
   highest-intent moment in the funnel. Now it refuses what is plainly not a
   photograph and hands everything else to the bytes.

   Two rules worth stating out loud:

   1. Every limit and every message comes from the DOM. Nothing about what is
      acceptable, and no sentence a customer can read, is written here. The
      section owns all of that so the admin can change it.

   2. The state rendered by the server is never overwritten on load. Only a
      customer action moves the field from one state to the next. See
      CONTRACT.md §5.3.

   Vanilla, no dependencies, same shape as file-field.js. */

(function () {
  'use strict';

  var BYTES_PER_KB = 1024;
  var BYTES_PER_MB = 1048576;

  /* The wire format of properties[_photo_meta] — width x height x bytes, from
     CONTRACT.md §5.2. A separator, not copy. */
  var META_SEPARATOR = '×';

  /* HOW LONG SILENCE IS ALLOWED TO LAST, and why this is not request.timeout.

     XMLHttpRequest's own timeout runs from send(), so any value long enough for
     a 15MB photograph on a 750 kbit/s connection — measured at 2m41s — is far
     too long to catch a stall, and any value short enough to catch one kills
     good uploads. What we want to know is that nothing has moved for a while,
     so this timer is reset by every progress event and cleared by the answer.

     The handler this replaces was written and then never reached: request.timeout
     was never assigned, so ontimeout was dead code and a stalled upload said
     "Sending your photo…" for ever. */
  var STALL_MS = 90000;

  /* The copy this page sends when a photograph is bigger than the studio needs.
     4000px on the long side at this quality is well above a framed 8x12 print
     at 300dpi, and turns minutes of uploading on a phone into seconds. The size
     itself is a section setting, because it is a studio decision; the quality
     is not, because there is no sensible reason to move it. */
  var RESIZE_QUALITY = 0.92;

  /* A little slack on that size, so a 4032px phone photograph is not re-encoded
     to gain 32 pixels. A second JPEG generation costs more than a 0.8%
     reduction is worth, and the studio is the one who pays for it. */
  var EDGE_SLACK = 1.15;

  /* How far over the size limit a file may be and still be worth decoding in
     the hope of rescuing it. Above this the decode itself is the risk — a
     phone browser asked for several hundred megabytes of pixels — and the
     answer would almost certainly still be "too heavy". NOT MEASURED on a
     low-memory device: if the page starts dying on large scans, this is the
     first number to lower. */
  var RESCUE_CEILING = 4;

  function attr(el, name) {
    return el.getAttribute('data-pm-upload-' + name) || '';
  }

  function whole(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  }

  function extensionOf(name) {
    var dot = name.lastIndexOf('.');
    return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
  }

  /* ------------------------------------------------------ instrumentation */

  /* WHY THIS FILE REPORTS, AND WHY IT IS THE ONLY ONE THAT DOES.

     Shopify's own funnel runs sessions -> product view -> added to cart ->
     reached checkout -> paid. Between the product view and the cart this shop
     hides a step no other Shopify store has, and the buy button stays shut
     until that step succeeds. So a 22MB scan refused as too heavy, a HEIC the
     picker would not even offer, and a stalled upload on hotel wifi all arrive
     in Shopify analytics as the same thing: viewed the product and left —
     indistinguishable from someone who simply did not like the price.

     That is also why this had to ship before the rest of the rebuild rather
     than after it. At a 1.1% baseline conversion is not a measurable quantity:
     two-proportion, 95%/80%, detecting +20% relative needs 38,768 sessions per
     variant. The transitions below run at 15-40%, which is where the samples
     are small enough to learn something from inside a quarter. Every other
     hypothesis on the list is an opinion until these numbers exist.

     Two constraints of the Web Pixels API, and they are why this is shaped the
     way it is. Only CUSTOM events may be published, so every name is
     namespaced — a standard name is simply dropped. And any visitor can
     publish these from their own console, so this is a funnel instrument and
     never a source of truth: nothing is billed, fulfilled or promised on the
     strength of one. The subscriber is one custom pixel in the admin, not an
     app and not a script in this theme.

     NOTHING HERE MAY EVER COST AN ORDER. Shopify.analytics does not exist in
     the preview harness, and on a storefront whose scripts failed to load it
     will not exist either; publish() is also free to throw. So the whole call
     is guarded and swallowed. This file is the only thing standing between a
     stranger and the most irreversible act on the site — a datapoint does not
     get to interrupt that. */
  function report(name, payload) {
    try {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== 'function') return;
      analytics.publish('photomotif:upload_' + name, payload);
    } catch (error) {
      /* Measurement is never worth the order it would have broken. */
    }
  }

  function bind(root) {
    if (root.getAttribute('data-pm-upload-ready')) return;
    root.setAttribute('data-pm-upload-ready', 'true');

    var input = root.querySelector('[data-pm-upload-input]');
    if (!input) return;

    /* The buy button sits outside the field, at the end of the form. */
    var form = root.closest ? root.closest('form') : null;
    var scope = form || root;

    var picker = root.querySelector('[data-pm-upload-pick]');
    var feedback = root.querySelector('[data-pm-upload-feedback]');
    var shot = root.querySelector('[data-pm-upload-zoom]');
    var thumb = root.querySelector('[data-pm-upload-thumb]');
    var thumbNote = root.querySelector('[data-pm-upload-thumb-note]');
    var resizedNote = root.querySelector('[data-pm-upload-resized]');
    var nameEl = root.querySelector('[data-pm-upload-name]');
    var sizeEl = root.querySelector('[data-pm-upload-size]');
    var removeButton = root.querySelector('[data-pm-upload-remove]');
    var retryButton = root.querySelector('[data-pm-upload-retry]');
    var urlField = root.querySelector('[data-pm-upload-url]');
    var metaField = root.querySelector('[data-pm-upload-meta]');
    var previewField = root.querySelector('[data-pm-upload-preview-url]');
    var progressEl = root.querySelector('[data-pm-upload-progress]');
    var messages = root.querySelectorAll('[data-pm-upload-error]');
    var submit = scope.querySelector('[data-pm-upload-submit]');
    var submitNote = scope.querySelector('[data-pm-upload-note]');
    var submitHint = scope.querySelector('[data-pm-upload-hint]');

    var accept = attr(root, 'accept');
    var maxBytes = whole(attr(root, 'max-bytes'), 0);
    var minPixels = whole(attr(root, 'min-pixels'), 0);
    var longEdge = whole(attr(root, 'downscale'), 0);
    var endpoint = attr(root, 'endpoint').replace(/^\s+|\s+$/g, '');
    var unitKb = attr(root, 'unit-kb');
    var unitMb = attr(root, 'unit-mb');

    var canPreview = Boolean(window.URL && window.URL.createObjectURL);
    var canDraw = Boolean(
      window.HTMLCanvasElement &&
      typeof document.createElement('canvas').toBlob === 'function'
    );

    var objectUrl = null;
    var request = null;
    var stall = null;
    var chosen = null;
    var carried = null;
    var measured = null;
    var sample = null;
    var guarded = false;

    /* ------------------------------------------------------------- state */

    /* One rule, and it is deliberately not about the state: there is nothing to
       buy until the backend has answered with a file id. A file that has been
       chosen, previewed and measured but not yet stored is not an order anyone
       could honour — the studio would receive a paid order and no photograph. */
    function held() {
      return Boolean(urlField && urlField.value);
    }

    /* THE BUY BUTTON IS NEVER A DEAD TAP.

       The server renders it with the native disabled attribute, which is the
       whole truth without JavaScript: nothing can be uploaded, so nothing can
       be bought. With the script the attribute is the wrong tool. A native
       disabled control swallows the tap — no click, no focus, no message — on
       the one control the visitor came to this page to press, and the nearest
       words sit 69px away and say nothing about a photograph being required.

       So the button keeps its shape and gives up the attribute: aria-disabled
       for anyone listening, a submit guard on the form for everyone else, and
       an answer in place of the note underneath. It still refuses, and it
       refuses on the stored file id rather than on the state — the same
       condition the server used. */
    function syncSubmit() {
      if (!submit) return;

      if (!guarded) {
        submit.disabled = !held();
        return;
      }

      submit.setAttribute('aria-disabled', held() ? 'false' : 'true');
    }

    function setState(next, key) {
      root.setAttribute('data-pm-upload-state', next);
      if (next !== 'uploading') phase('');

      for (var i = 0; i < messages.length; i++) {
        var message = messages[i];
        if (next === 'error' && message.getAttribute('data-pm-upload-error') === key) {
          message.removeAttribute('hidden');
        } else {
          message.setAttribute('hidden', 'hidden');
        }
      }

      /* Whatever the button last complained about, the field has moved on. */
      putBack();
      syncSubmit();
    }

    /* The second half of an upload has no progress to show: the bytes are all
       there and Shopify is processing the file, measured at four to five
       seconds in backend/src/upload.mjs. The phase is what the section reads to
       swap the sentence and set the bar breathing. */
    function phase(name) {
      if (name) root.setAttribute('data-pm-upload-phase', name);
      else root.removeAttribute('data-pm-upload-phase');
    }

    /* ------------------------------------------------------- saying so */

    /* Feedback nobody can see is not feedback. The OS picker covers the whole
       screen on a phone, and it closes on a page scrolled wherever it was
       left; the preview and the refusals both live in one slot under the
       picker, and this is what makes sure that slot is actually on screen.

       Only when it is not already: moving the page under someone who can
       already see the thing they are waiting for is worse than doing nothing.
       prefers-reduced-motion drops the animation, not the scroll — a page that
       jumps still beats an answer below the fold. */
    function bringIntoView(el) {
      if (!el || !el.getBoundingClientRect || !el.scrollIntoView) return;

      var box = el.getBoundingClientRect();
      var height = window.innerHeight || document.documentElement.clientHeight;
      if (box.top >= 0 && box.bottom <= height) return;

      var calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      try {
        el.scrollIntoView({ block: 'center', behavior: calm ? 'auto' : 'smooth' });
      } catch (error) {
        /* Older Safari takes no options object and scrolls anyway. */
        el.scrollIntoView();
      }
    }

    function askForPhoto() {
      if (submitNote) submitNote.hidden = true;
      if (submitHint) submitHint.hidden = false;

      /* No photograph at all: the picker is what is missing, so go there and
         put the keyboard on it. One in flight: the answer is in the feedback
         slot, and taking focus away from a live status line to a file input
         would be a worse answer than the one it replaced. */
      if (!chosen) {
        bringIntoView(picker || input);
        if (input.focus) input.focus();
        return;
      }
      bringIntoView(feedback);
    }

    function putBack() {
      if (submitHint) submitHint.hidden = true;
      if (submitNote) submitNote.hidden = false;
    }

    function onSubmit(event) {
      if (held()) return;

      /* Nothing stored, so there is no photograph and there is nothing to
         sell. preventDefault only, and no stopPropagation: another listener on
         this form may legitimately want to hear that this happened. */
      event.preventDefault();
      askForPhoto();
    }

    /* ----------------------------------------------------------- preview */

    function release() {
      if (!objectUrl) return;
      window.URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    }

    function readableSize(bytes) {
      if (bytes >= BYTES_PER_MB) {
        return Math.round((bytes / BYTES_PER_MB) * 10) / 10 + ' ' + unitMb;
      }
      return Math.max(1, Math.round(bytes / BYTES_PER_KB)) + ' ' + unitKb;
    }

    function showPreview(file) {
      if (nameEl) nameEl.textContent = file.name;
      if (sizeEl) sizeEl.textContent = readableSize(file.size);
      if (resizedNote) resizedNote.hidden = true;

      if (canPreview && thumb) {
        release();
        objectUrl = window.URL.createObjectURL(file);
        /* Put the frame back before asking it to draw: the error handler below
           may take it away again a moment later, and the previous file may
           have left it hidden. */
        if (thumbNote) thumbNote.hidden = true;
        if (shot) shot.hidden = false;
        thumb.removeAttribute('hidden');
        thumb.src = objectUrl;
      }

      root.setAttribute('data-pm-upload-preview', 'true');
    }

    function clearPreview() {
      release();
      chosen = null;
      carried = null;
      measured = null;
      sample = null;
      input.value = '';

      if (thumb) {
        thumb.removeAttribute('src');
        thumb.setAttribute('hidden', 'hidden');
      }
      if (shot) {
        shot.hidden = false;
        shot.setAttribute('aria-expanded', 'false');
      }
      if (thumbNote) thumbNote.hidden = true;
      if (resizedNote) resizedNote.hidden = true;
      if (nameEl) nameEl.textContent = '';
      if (sizeEl) sizeEl.textContent = '';
      root.removeAttribute('data-pm-upload-zoomed');

      forget();
      root.removeAttribute('data-pm-upload-preview');
    }

    /* The three hidden fields describe a photograph that is in Files. The
       moment they stop describing what is on screen they are cleared, which is
       also what shuts the buy button again. */
    function forget() {
      if (urlField) urlField.value = '';
      if (metaField) metaField.value = '';
      if (previewField) previewField.value = '';
    }

    function progress(percent) {
      if (!progressEl) return;
      progressEl.style.setProperty('--pm-upload-progress', percent + '%');
      progressEl.setAttribute('aria-valuenow', percent);
    }

    /* ------------------------------------------------------------ checks */

    /* WHAT THIS CHECK IS FOR, AND WHAT IT IS NO LONGER FOR.

       It is not the authority on what can be restored. backend/src/image.mjs
       is: it reads the bytes, which are the only evidence, and it is stricter
       than any list of media types can be — a .jpg that is really a PDF passes
       every check a browser can run.

       What this is good at is saving a doomed round trip: a text file, a PDF,
       a video, a 30MB archive. So a type that is plainly not a photograph is
       refused here, an image type this list does not happen to name goes to
       the sniffer, and a file the browser could not name at all goes there
       too. HEIC arrives with an empty type in more than one browser, and
       image/heif and image/heic-sequence are what an iPhone hands over for a
       generic HEIF, a burst and a Live Photo — all three accepted by the
       backend, all three refused here until this stopped testing equality. */
    function accepted(file) {
      var wanted = accept.split(',');
      var type = (file.type || '').toLowerCase();
      var extension = extensionOf(file.name || '');

      for (var i = 0; i < wanted.length; i++) {
        var want = wanted[i].replace(/^\s+|\s+$/g, '').toLowerCase();
        if (!want) continue;

        if (want.charAt(0) === '.') {
          if (extension === want.slice(1)) return true;
          continue;
        }

        if (want.slice(-2) === '/*') {
          if (type && type.indexOf(want.slice(0, -1)) === 0) return true;
          continue;
        }

        if (type === want) return true;

        /* No type to compare: the name is all there is. */
        if (!type) {
          var subtype = want.split('/')[1] || '';
          if (extension === subtype) return true;
          if (subtype === 'jpeg' && extension === 'jpg') return true;
          if (subtype === 'tiff' && extension === 'tif') return true;
        }
      }

      if (!type) return true;
      if (type.indexOf('image/') === 0) return true;
      /* What a browser says when it has no idea, which is not the same as
         saying the file is wrong. */
      if (type === 'application/octet-stream' || type === 'binary/octet-stream') return true;

      return false;
    }

    /* ------------------------------------------------------- measurement */

    /* WHAT A FORMAT IS ALLOWED TO BE CALLED IN AN EVENT.

       The browser's own media type when it has one. When it has none — which
       is how HEIC arrives in more than one browser, and HEIC is the format the
       shop most needs to count — the extension stands in, but only when the
       studio's own accept list already names it. That bound is the whole point
       of the function: the accept list is a fixed vocabulary the studio wrote,
       so no word out of a customer's filename can escape through this field.
       An unrecognised extension reports as nothing rather than as itself. */
    function formatOf(file) {
      var type = (file.type || '').toLowerCase();
      if (type) return type;

      var extension = extensionOf(file.name || '');
      if (!extension) return '';

      var wanted = accept.split(',');
      for (var i = 0; i < wanted.length; i++) {
        var want = wanted[i].replace(/^\s+|\s+$/g, '').toLowerCase();
        if (!want) continue;
        if (want.charAt(0) === '.' && want.slice(1) === extension) return extension;
        if (want.split('/')[1] === extension) return extension;
      }
      return '';
    }

    /* THE ONLY SHAPE THAT EVER LEAVES THIS PAGE, and the list is short on
       purpose: weight, dimensions, format, and which refusal it was. Never the
       filename — a family photograph is very often named after the person in
       it — never the pixels, never anything the customer typed. What is here
       sizes the refusals and nothing identifies anybody.

       It is read from `sample` rather than from the live selection because the
       live selection is cleared by three of the four refusals before the state
       even changes. `sample` is written once, at the moment of the pick and
       before any check has run, so a refusal is always measured against the
       file that caused it and never against whatever was on screen before. */
    function detail(key) {
      var payload = {
        bytes: sample ? sample.bytes : 0,
        width: sample ? sample.width : 0,
        height: sample ? sample.height : 0,
        format: sample ? sample.format : ''
      };
      if (key) payload.error = key;
      return payload;
    }

    /* ONE DECODE, USED TWICE: for the dimensions the too-small check needs, and
       for the smaller copy below. Decoding a 20MB photograph twice on a phone
       is not free, and this is the only place in the file that can fail
       silently — a file the browser cannot decode is not a small photo. TIFF
       decodes in no browser and HEIC only in Safari, and both are formats this
       shop invites, so a failure here lets the file through and leaves the
       judgement to the studio. */
    function withDecoded(file, use) {
      if (!canPreview || !window.Image) {
        use(null, function () {});
        return;
      }

      var probe = window.URL.createObjectURL(file);
      var image = new window.Image();
      var spent = false;

      function hand(ok) {
        if (spent) return;
        spent = true;
        /* The caller releases it, once it has finished drawing from it. */
        use(ok ? image : null, function () { window.URL.revokeObjectURL(probe); });
      }

      image.onload = function () { hand(image.naturalWidth > 0 && image.naturalHeight > 0); };
      image.onerror = function () { hand(false); };
      image.src = probe;
    }

    /* A COPY SMALL ENOUGH TO TRAVEL AND LARGE ENOUGH TO PRINT.

       A 15MB phone photograph took 2m41s at 750 kbit/s, with a progress bar
       nobody could see and no way to stop it. At 4000px on the long side it is
       typically five to ten times smaller, which is well above what an 8x12
       print at 300dpi needs — and it makes the "too heavy" refusal, which was
       costing whole orders on flatbed scans, nearly unreachable.

       Three things this deliberately does not do. It never upscales. It never
       sends the copy when the copy came out bigger than the original, which
       happens on a small PNG. And it never touches a file the browser could
       not decode, because a canvas cannot draw what the browser cannot read —
       which is also why this can never quietly send a blank rectangle: no
       decode, no copy, the original goes as it always did. */
    function smallerCopy(file, image, done) {
      if (!canDraw || !image) { done(null); return; }

      var width = image.naturalWidth;
      var height = image.naturalHeight;
      var longest = width > height ? width : height;
      if (!longest) { done(null); return; }

      var overSize = maxBytes > 0 && file.size > maxBytes;
      var overEdge = longEdge > 0 && longest > longEdge * EDGE_SLACK;
      if (!overSize && !overEdge) { done(null); return; }

      var scale = overEdge ? longEdge / longest : 1;

      try {
        var canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));

        var pen = canvas.getContext('2d');
        if (!pen) { done(null); return; }
        pen.drawImage(image, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          function (blob) {
            done(blob && blob.size < file.size ? blob : null);
          },
          'image/jpeg',
          RESIZE_QUALITY
        );
      } catch (error) {
        /* Out of memory, or a canvas the device refuses to allocate. The
           original is still perfectly sendable. */
        done(null);
      }
    }

    /* What Files should call the copy. The backend re-derives the extension
       from the bytes anyway, but a customer looking at their own order should
       recognise the name they chose. */
    function asJpeg(name) {
      var stem = name.replace(/\.[^.]*$/, '');
      return (stem || 'photograph') + '.jpg';
    }

    /* too_small, unsupported and too_large empty the selection: that file will
       never do. network keeps it, because nothing is wrong with it. */
    function refuse(key) {
      /* Before clearPreview, not after: it is the clear that throws away the
         weight and the format the event exists to carry. */
      report('refused', detail(key));
      clearPreview();
      setState('error', key);
      bringIntoView(feedback);
    }

    /* ---------------------------------------------------------- pre-upload */

    function parse(text) {
      try {
        return JSON.parse(text);
      } catch (error) {
        return null;
      }
    }

    function metaOf(answer, job) {
      var width = (answer && answer.width) || (measured && measured.width) || '';
      var height = (answer && answer.height) || (measured && measured.height) || '';
      var bytes = (answer && answer.bytes) || job.body.size || '';
      if (!width || !height) return '';
      return width + META_SEPARATOR + height + META_SEPARATOR + bytes;
    }

    /* Everything that ends an attempt goes through here, so no handler ever
       fires for a request nobody is waiting for any more, and the stall timer
       never outlives the request it was watching. */
    function stop() {
      if (stall) {
        window.clearTimeout(stall);
        stall = null;
      }
      if (!request) return;

      var running = request.readyState !== 4;
      request.onload = null;
      request.onerror = null;
      request.onabort = null;
      request.ontimeout = null;
      if (request.upload) {
        request.upload.onprogress = null;
        request.upload.onload = null;
      }
      if (running) request.abort();
      request = null;
    }

    function watch() {
      if (stall) window.clearTimeout(stall);
      stall = window.setTimeout(function () {
        stall = null;
        stop();
        /* Nothing was lost: the file is still selected, which is what the
           network message says and what the retry button is for. */
        setState('error', 'network');
        bringIntoView(feedback);
        /* `network` is the one key that does not go through refuse(), because
           it is the one refusal that keeps the file — CONTRACT.md 5.4. So it
           reports at each of the three places a send can die instead. */
        report('refused', detail('network'));
      }, STALL_MS);
    }

    function send(job) {
      if (!endpoint || !window.FormData || !window.XMLHttpRequest) return;

      stop();

      var payload = new FormData();
      payload.append('file', job.body, job.filename);

      progress(0);
      phase('sending');
      setState('uploading');
      bringIntoView(feedback);

      /* Fired again on a retry, deliberately: the question this answers is how
         many attempts it takes to get a photograph into Files, not how many
         customers reached the try. */
      report('started', detail());

      /* The call is held twice: in `request`, which is what stop() reaches for,
         and in a local, which is what the handlers read. They are the same
         object until stop() lets go of it — and stop() runs inside onload, so
         a handler reading `request` there would be reading null. */
      var call = new XMLHttpRequest();
      request = call;
      call.open('POST', endpoint, true);

      if (call.upload) {
        call.upload.onprogress = function (event) {
          if (!event.lengthComputable) return;
          progress(Math.round((event.loaded / event.total) * 100));
          watch();
        };

        /* THE HANDOVER FROM ONE WAIT TO THE OTHER, and it is this event rather
           than a percentage that marks it: upload.onload fires when the last
           byte of the body has gone, whereas progress events are a courtesy
           the browser may skip entirely on a small file or behind a proxy. The
           bar is at the end of its travel and nothing else will move it, so
           the sentence beside it has to take over — this is the few seconds
           backend/src/upload.mjs measured Shopify spending on the file, and
           the phase is what stops them reading as a page that has died. */
        call.upload.onload = function () {
          progress(100);
          phase('filing');
          watch();
        };
      }

      call.onload = function () {
        var answer = parse(call.responseText);
        var status = call.status;
        stop();

        if (status >= 200 && status < 300) {
          /* A Shopify file id, despite the field name — `gid://shopify/...`.
             Never a CDN URL: a URL cannot be looked up in Files, so a purge
             would never find the photograph again to delete it. */
          if (urlField) urlField.value = (answer && answer.url) || '';
          if (metaField) metaField.value = metaOf(answer, job);
          if (previewField) previewField.value = (answer && answer.preview) || '';
          progress(100);
          setState('uploaded');
          report('succeeded', detail());
          return;
        }

        /* A refusal the backend can name gets its own message; anything else,
           including a shape we cannot read, is a failure to reach us.
           CONTRACT.md §9.1. */
        var named = answer && answer.error;
        if (named === 'too_small' || named === 'unsupported' || named === 'too_large') {
          refuse(named);
          return;
        }

        setState('error', 'network');
        bringIntoView(feedback);
        report('refused', detail('network'));
      };

      call.onerror = function () {
        stop();
        setState('error', 'network');
        bringIntoView(feedback);
        report('refused', detail('network'));
      };

      call.send(payload);
      watch();
    }

    /* -------------------------------------------------------------- events */

    /* What actually goes on the wire: the file itself, or the smaller copy. */
    function carry(body, filename, resized) {
      carried = { body: body, filename: filename, resized: Boolean(resized) };
      if (resizedNote) resizedNote.hidden = !resized;

      /* From here the weight that matters is the one going on the wire, which
         on a resized scan is not the weight that was picked. The too_large
         refusal below is judged on it, so the event has to be too. */
      if (sample) sample.bytes = body.size;

      /* The weight is judged on what will be sent, so a scan that only needed
         resizing is never refused for the size it arrived at. The refusal
         itself is unchanged: type first, then weight, then dimensions —
         CONTRACT.md §5.4 — and this is still the weight check, just applied to
         the thing being weighed. */
      if (maxBytes > 0 && body.size > maxBytes) {
        refuse('too_large');
        return;
      }

      if (endpoint) send(carried);
    }

    function onChange() {
      var file = input.files && input.files[0];

      if (!file) {
        clearPreview();
        setState('empty');
        return;
      }

      /* Recorded before the first check runs, so that every refusal below is
         counted against the same denominator: files picked. A file refused for
         its type never reaches setState, and instrumenting the states alone
         would have left the commonest refusal out of the funnel it belongs
         to. Dimensions are zero because nothing has been decoded yet, and a
         decode is what they cost. */
      sample = { bytes: file.size, width: 0, height: 0, format: formatOf(file) };
      report('picked', detail());

      /* Type before weight. A 30MB text file is not a heavy photograph, and
         telling someone to export it smaller sends them off to fix the wrong
         thing. What kind of file it is decides everything else. */
      if (accept && !accepted(file)) {
        refuse('unsupported');
        return;
      }

      /* So far past the limit that no copy of it would fit either, and far
         enough that decoding it to find out is the bigger risk. */
      if (maxBytes > 0 && file.size > maxBytes * RESCUE_CEILING) {
        refuse('too_large');
        return;
      }

      chosen = file;
      carried = null;
      showPreview(file);
      /* The previous id, if any, no longer describes what is on screen.
         Clearing it shuts the buy button until this file has been stored. */
      forget();
      setState('selected');
      bringIntoView(feedback);

      withDecoded(file, function (image, spend) {
        /* A newer choice landed while this one was being decoded. */
        if (chosen !== file) {
          spend();
          return;
        }

        measured = image ? { width: image.naturalWidth, height: image.naturalHeight } : null;
        if (sample && measured) {
          sample.width = measured.width;
          sample.height = measured.height;
        }

        /* Dimensions last, because they cost a decode, and only when there is
           a number to judge — an undecodable file is not a small one. */
        if (measured && minPixels > 0) {
          var longest = measured.width > measured.height ? measured.width : measured.height;
          if (longest && longest < minPixels) {
            spend();
            refuse('too_small');
            return;
          }
        }

        smallerCopy(file, image, function (copy) {
          spend();
          if (chosen !== file) return;
          if (copy) carry(copy, asJpeg(file.name), true);
          else carry(file, file.name, false);
        });
      });
    }

    input.addEventListener('change', onChange);

    if (removeButton) {
      /* One button, two jobs. During the send it is Cancel and it stops the
         send; the rest of the time it is Remove. Both end in the same place,
         which is why it is one control: nothing is with the studio, so the
         field is empty and the picker is the call to action again. */
      removeButton.addEventListener('click', function () {
        /* Before the clear, for the same reason refuse() reports before it.
           Guarded on `sample` so this counts a photograph actually taken back
           — the other route to `empty`, a picker dismissed with nothing in it,
           is not a removal and is not an event. */
        if (sample) report('removed', detail());
        stop();
        clearPreview();
        setState('empty');
        input.focus();
      });
    }

    if (retryButton) {
      retryButton.addEventListener('click', function () {
        if (carried) {
          send(carried);
          return;
        }
        var file = chosen || (input.files && input.files[0]);
        if (file) send({ body: file, filename: file.name, resized: false });
      });
    }

    if (shot) {
      /* A thumbnail is not enough to be sure which of two similar family
         photographs this is, and the next tap pays for work on it. */
      shot.addEventListener('click', function () {
        var open = root.getAttribute('data-pm-upload-zoomed') === 'true';
        if (open) root.removeAttribute('data-pm-upload-zoomed');
        else root.setAttribute('data-pm-upload-zoomed', 'true');
        shot.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    }

    if (thumb) {
      /* NEVER A BROKEN IMAGE GLYPH IN THE FRAME. TIFF decodes in no browser
         and HEIC only in Safari; both are formats this shop asks for, and both
         used to land here as the browser's own torn-page icon beside the words
         "The photo you chose". The measuring probe above has handled a failed
         decode since it was written — the thumbnail did not. */
      thumb.addEventListener('error', function () {
        thumb.setAttribute('hidden', 'hidden');
        if (shot) shot.hidden = true;
        if (thumbNote) thumbNote.hidden = false;
      });
    }

    /* THE GUARD GOES ON BEFORE THE ATTRIBUTE COMES OFF, and that order is the
       whole safety of it: there is no instant in which this button is live and
       unguarded. An order that arrives paid with no photograph is the failure
       the whole upload architecture exists to prevent.

       Left alone when the page has nothing to sell: there the server's disabled
       is the truth of the page, not a statement about the photograph. */
    if (form && submit && scope.querySelector('input[name="id"]:checked')) {
      form.addEventListener('submit', onSubmit);
      guarded = true;
      submit.disabled = false;
    }

    /* An object URL outlives the element that used it, so it is given back on
       the way out as well as on every replacement. */
    window.addEventListener('pagehide', release);

    /* The only thing touched on load: the state itself is left exactly as the
       server rendered it. */
    syncSubmit();
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-pm-upload]');
    for (var i = 0; i < roots.length; i++) bind(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
